import { z } from "zod";
import type { ExerciseType } from "./exercise-types";

// Shared by the learner-profile explanation and server-side start routing.
// Extend together with the material filter, preview and start-path tests.
export const READING_PROGRESSION_EXERCISES = {
  blitzlesen: [
    { label: "Blitzlesen – Wörter", effect: "Filtert den Grundwortschatz und redaktionelle Wortlisten nach den freigegebenen Leseeinheiten und Schreibmustern." },
    { label: "Blitzlesen – Silben", effect: "Filtert einfache offene Silben und Silben mit Konsonantenverbindungen nach denselben Freigaben." },
    { label: "Blitzlesen – Pseudowörter", effect: "Filtert ein- und zweisilbige Pseudowörter nach denselben Freigaben." },
    { label: "Individuelle Blitzleseaufgaben", effect: "Filtert die zugewiesene eigene Wortliste. Einträge brauchen eine geprüfte Materialbeschreibung; eigene neue Einträge müssen fachlich bestätigt sein." },
    { label: "Adaptive Blitzleseaufträge", effect: "Filtert zuerst das Material. Erst danach werden passende Einträge anhand bisheriger Übungsergebnisse gewichtet." },
    { label: "Arbeitsblätter – Leselisten", effect: "Filtert bei ausgewähltem Lernkonto das veröffentlichte globale Lesematerial vor der PDF-Erstellung nach den aktuellen Freigaben. Vorschau und Download zeigen dieselben Einträge ohne Wiederholungen." },
  ],
  silbentrennung: [
    { label: "Lesesilben trennen (Silbentrennung)", effect: "Filtert die Wörter der gewählten Silbenanzahl. Die Hervorhebung von Buchstabenverbindungen berücksichtigt bei aktivem Profil die geprüften Segmentgrenzen." },
    { label: "Arbeitsblätter – Silbentrennung", effect: "Filtert bei ausgewähltem Lernkonto die Wörter der gewählten Silbenanzahl auch für die PDF-Vorschau. Leere Pools verhindern die Ausgabe; es wird kein Lernlauf gespeichert." },
  ],
} as const satisfies Partial<Record<ExerciseType, readonly { label: string; effect: string }[]>>;

export function supportsReadingProgression(exerciseType: string) {
  return Object.hasOwn(READING_PROGRESSION_EXERCISES, exerciseType);
}

export const READING_CATALOG_VERSION = 1 as const;
const letters = "abcdefghijklmnopqrstuvwxyzäöüß".split("");
const connections = ["sch", "ch", "ei", "ai", "au", "eu", "äu", "ie", "ng", "nk", "ck", "tz", "qu", "pf", "chs", "tsch", "ph", "th", "sp", "st"];
const examples = ["Schaf", "Buch / ich", "Eis", "Mai", "Maus", "Eule", "Häuser", "Biene", "Ring", "Bank", "Decke", "Katze", "Quelle", "Apfel", "Fuchs", "Quatsch", "Foto: ph in Phase", "Thema", "Spiegel", "Stein"];
export const READING_UNITS = [
  ...letters.map((id) => ({ id, label: `${id === "ß" ? "ẞ" : id.toLocaleUpperCase("de-DE")}/${id}`, group: "letters" as const, example: "" })),
  ...connections.map((id, index) => ({ id, label: id, group: "connections" as const, example: examples[index]! })),
  { id: "double-consonant", label: "Doppelkonsonanten", group: "patterns" as const, example: "Sonne, Ball" },
  { id: "double-vowel", label: "Doppelvokale", group: "patterns" as const, example: "Boot, See" },
  { id: "silent-h", label: "Dehnungs-h", group: "patterns" as const, example: "Zahn, Uhr" },
  { id: "syllable-h", label: "Silbentrennendes h", group: "patterns" as const, example: "gehen, Ruhe" },
];
export const readingUnitIdSchema = z.enum(READING_UNITS.map(({ id }) => id) as [string, ...string[]]);
export const readingStatusSchema = z.enum(["not-introduced", "practicing", "secured"]).nullable();
export const readingStatusLabels = { "not-introduced": "Noch nicht eingeführt", practicing: "In Übung", secured: "Gesichert" };
const uniqueUnits = (values: string[]) => new Set(values).size === values.length;
const entrySchema = z.object({ unitId: readingUnitIdSchema, allowed: z.boolean(), status: readingStatusSchema });
export const readingProfileSchema = z.object({ schemaVersion: z.literal(1), enabled: z.boolean(), entries: z.array(entrySchema).max(READING_UNITS.length) })
  .refine((profile) => uniqueUnits(profile.entries.map(({ unitId }) => unitId)), "Jede Einheit darf nur einmal vorkommen.");
export type ReadingProfile = z.infer<typeof readingProfileSchema>;
export const emptyReadingProfile = (): ReadingProfile => ({ schemaVersion: 1, enabled: false, entries: [] });
export const readingProfileUpdateSchema = z.object({
  revision: z.number().int().nonnegative(), enabled: z.boolean().optional(),
  entries: z.array(z.object({ unitId: readingUnitIdSchema, allowed: z.boolean().optional(), status: readingStatusSchema.optional() })).max(READING_UNITS.length),
}).refine((value) => uniqueUnits(value.entries.map(({ unitId }) => unitId)), "Jede Einheit darf nur einmal vorkommen.");
export type ReadingProfileUpdate = z.infer<typeof readingProfileUpdateSchema>;

export function updateReadingProfile(previous: ReadingProfile, update: ReadingProfileUpdate): ReadingProfile {
  const entries = new Map(previous.entries.map((entry) => [entry.unitId, entry]));
  for (const change of update.entries) {
    const old = entries.get(change.unitId) ?? { unitId: change.unitId, allowed: false, status: null };
    entries.set(change.unitId, {
      unitId: change.unitId, status: change.status === undefined ? old.status : change.status,
      allowed: change.allowed ?? (change.status === "practicing" && old.status !== "practicing" ? true : old.allowed),
    });
  }
  return { schemaVersion: 1, enabled: update.enabled ?? previous.enabled, entries: [...entries.values()] };
}

export const readingSnapshotSchema = z.object({ schemaVersion: z.literal(1), catalogVersion: z.literal(1), enabled: z.boolean(), revision: z.number().int().nonnegative(), allowedUnits: z.array(readingUnitIdSchema).max(READING_UNITS.length) });
export type ReadingSnapshot = z.infer<typeof readingSnapshotSchema>;
export function readingSnapshot(profile: ReadingProfile, revision: number): ReadingSnapshot {
  return { schemaVersion: 1, catalogVersion: READING_CATALOG_VERSION, revision, enabled: profile.enabled, allowedUnits: profile.entries.filter(({ allowed }) => allowed).map(({ unitId }) => unitId) };
}
export function normalizeReadingText(text: string) { return text.normalize("NFC").trim().toLocaleLowerCase("de-DE"); }

// A segment denotes its written surface. Only the two h markers have another ID.
export const readingSegmentSchema = z.enum(["a", ...letters.slice(1), ...connections, "h:silent", "h:syllable", "-"]);
export const readingMaterialSchema = z.object({ schemaVersion: z.literal(1), text: z.string().trim().min(1).max(120), segments: z.array(readingSegmentSchema).min(1).max(120), reviewed: z.boolean() })
  .refine((entry) => entry.segments.map((segment) => segment.startsWith("h:") ? "h" : segment).join("") === normalizeReadingText(entry.text), "Die Leseeinheiten müssen den Eintrag vollständig ergeben.");
export type ReadingMaterial = z.infer<typeof readingMaterialSchema>;
export const readingMaterialsSchema = z.array(readingMaterialSchema).max(5_000).refine((entries) => uniqueUnits(entries.map(({ text }) => normalizeReadingText(text))), "Jeder Materialeintrag darf nur einmal beschrieben werden.");

/** Suggestions are never reviewed automatically. Word-specific pronunciation needs editorial confirmation. */
export function suggestReadingMaterial(text: string): ReadingMaterial {
  const lower = normalizeReadingText(text);
  const segments: string[] = [];
  const clusters = [...connections].sort((a, b) => b.length - a.length);
  let index = 0;
  while (index < lower.length) {
    const cluster = clusters.find((unit) => lower.startsWith(unit, index) && (!["sp", "st"].includes(unit) || index === 0));
    const unit = cluster ?? lower[index]!;
    segments.push(unit); index += unit.length;
  }
  return { schemaVersion: 1, text, segments, reviewed: false };
}
export function readingRequirements(material: ReadingMaterial): string[] {
  const required = new Set<string>();
  material.segments.forEach((segment, index) => {
    if (segment === "-") return;
    required.add(segment === "h:silent" ? "silent-h" : segment === "h:syllable" ? "syllable-h" : segment);
    if (segment.length === 1 && segment === material.segments[index - 1]) required.add("aeiouäöü".includes(segment) ? "double-vowel" : "double-consonant");
  });
  return [...required];
}
export interface ReadingPreview {
  enabled: boolean; total: number; eligible: number;
  excluded?: Array<{ text: string; reason: string }>;
}
